const LEASE_KEY = "internet-traffic-jam:primary-tab:v1";
const LEASE_MILLISECONDS = 6_000;
const RENEW_MILLISECONDS = 2_000;

type Lease = {
  ownerId: string;
  expiresAt: number;
};

type PrimaryListener = (primary: boolean) => void;

function parseLease(value: string | null): Lease | null {
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "ownerId" in parsed &&
      "expiresAt" in parsed &&
      typeof parsed.ownerId === "string" &&
      typeof parsed.expiresAt === "number"
    ) {
      return { ownerId: parsed.ownerId, expiresAt: parsed.expiresAt };
    }
  } catch {
    // Invalid leases are treated as expired.
  }
  return null;
}

export class PrimaryTabCoordinator {
  private readonly tabId = crypto.randomUUID();
  private readonly listeners = new Set<PrimaryListener>();
  private readonly channel =
    "BroadcastChannel" in window
      ? new BroadcastChannel("internet-traffic-jam:tabs")
      : null;
  private interval: number | null = null;
  private primary = false;

  public start(): void {
    if (this.interval !== null) return;
    this.channel?.addEventListener("message", this.handleChannelMessage);
    window.addEventListener("storage", this.handleStorage);
    window.addEventListener("pagehide", this.handlePageHide);
    document.addEventListener("visibilitychange", this.evaluate);
    this.evaluate();
    this.interval = window.setInterval(this.evaluate, RENEW_MILLISECONDS);
  }

  public stop(): void {
    if (this.interval !== null) window.clearInterval(this.interval);
    this.interval = null;
    this.release();
    this.channel?.removeEventListener("message", this.handleChannelMessage);
    this.channel?.close();
    window.removeEventListener("storage", this.handleStorage);
    window.removeEventListener("pagehide", this.handlePageHide);
    document.removeEventListener("visibilitychange", this.evaluate);
  }

  public isPrimary(): boolean {
    return this.primary;
  }

  public claimPrimary(): boolean {
    if (document.hidden) return false;
    const nextLease: Lease = {
      ownerId: this.tabId,
      expiresAt: Date.now() + LEASE_MILLISECONDS,
    };
    localStorage.setItem(LEASE_KEY, JSON.stringify(nextLease));
    const confirmed = parseLease(localStorage.getItem(LEASE_KEY));
    this.setPrimary(confirmed?.ownerId === this.tabId);
    this.channel?.postMessage({ type: "lease-updated" });
    return this.primary;
  }

  public subscribe(listener: PrimaryListener): () => void {
    this.listeners.add(listener);
    listener(this.primary);
    return () => this.listeners.delete(listener);
  }

  private readonly evaluate = (): void => {
    if (document.hidden) {
      const lease = parseLease(localStorage.getItem(LEASE_KEY));
      if (lease?.ownerId === this.tabId) {
        localStorage.removeItem(LEASE_KEY);
        this.channel?.postMessage({ type: "lease-updated" });
      }
      this.setPrimary(false);
      return;
    }
    const now = Date.now();
    const lease = parseLease(localStorage.getItem(LEASE_KEY));
    if (!lease || lease.expiresAt <= now || lease.ownerId === this.tabId) {
      const nextLease: Lease = {
        ownerId: this.tabId,
        expiresAt: now + LEASE_MILLISECONDS,
      };
      localStorage.setItem(LEASE_KEY, JSON.stringify(nextLease));
      const confirmed = parseLease(localStorage.getItem(LEASE_KEY));
      this.setPrimary(confirmed?.ownerId === this.tabId);
      this.channel?.postMessage({ type: "lease-updated" });
      return;
    }
    this.setPrimary(false);
  };

  private readonly handleStorage = (event: StorageEvent): void => {
    if (event.key === LEASE_KEY) this.evaluate();
  };

  private readonly handleChannelMessage = (): void => {
    this.evaluate();
  };

  private readonly handlePageHide = (event: PageTransitionEvent): void => {
    if (event.persisted) return;
    this.release();
  };

  private release(): void {
    const lease = parseLease(localStorage.getItem(LEASE_KEY));
    if (lease?.ownerId === this.tabId) {
      localStorage.removeItem(LEASE_KEY);
      this.channel?.postMessage({ type: "lease-updated" });
    }
    this.setPrimary(false);
  }

  private setPrimary(primary: boolean): void {
    if (primary === this.primary) return;
    this.primary = primary;
    this.listeners.forEach((listener) => listener(primary));
  }
}
